/**
 * StudioManagementPage - 스튜디오 관리 페이지
 * 
 * 접근 권한: can_access_commission = true인 사용자
 * 
 * 기능:
 * - 메인 플로팅 메뉴 위에 서브메뉴 표시
 * - STOCKS, COMMISSIONS, PARAMÈTRES 세 개의 서브메뉴
 */
import React, { useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { getThemeForPage } from '../styles/themes';
import { hasCommissionAccess } from '../utils/permissionConfig';
import styles from '../styles/StudioManagementStyles.module.css';

const TEXT = {
    title: { ko: '스튜디오 관리', en: 'Studio Management', fr: 'Gestion du Studio' },
    noAccess: { ko: '접근 권한이 없습니다', en: 'No access permission', fr: 'Aucune autorisation d\'accès' },
    // 메뉴 아이템
    stocks: { ko: '재고', en: 'STOCKS', fr: 'STOCKS' },
    commissions: { ko: '커미션', en: 'COMMISSIONS', fr: 'COMMISSIONS' },
    parameters: { ko: '설정', en: 'PARAMETERS', fr: 'PARAMÈTRES' },
    comingSoon: { ko: '준비 중', en: 'Coming Soon', fr: 'Bientôt' },
};

const t = (key, lang) => TEXT[key]?.[lang] || TEXT[key]?.['ko'] || key;

export default function StudioManagementPage() {
    const { userInfo } = useContext(UserContext);
    const navigate = useNavigate();
    const lang = userInfo?.preferred_language || 'ko';
    const currentTheme = getThemeForPage('/studio-management');

    // 접근 권한 체크 (can_access_commission 컬럼 기반)
    const hasAccess = hasCommissionAccess(userInfo);

    const menuItems = [
        {
            id: 'stocks',
            textKey: 'stocks',
            enabled: false,
            path: null,
        },
        {
            id: 'commissions',
            textKey: 'commissions',
            enabled: true,
            path: '/commission-management',
        },
        {
            id: 'parameters',
            textKey: 'parameters',
            enabled: false,
            path: null,
        },
    ];

    const handleMenuClick = (item) => {
        if (item.enabled && item.path) {
            navigate(item.path);
        }
    };

    // 접근 권한 없음
    if (!hasAccess) {
        return (
            <div className={styles.noAccess}>
                {t('noAccess', lang)}
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* 페이지 제목 (상단) */}
            <h1
                className={styles.pageTitle}
                style={{ color: currentTheme?.activeColor }}
            >
                {t('title', lang)}
            </h1>

            {/* 서브메뉴 (플로팅 메뉴 위에 배치) */}
            <div className={styles.submenuContainer}>
                {menuItems.map(item => (
                    <button
                        key={item.id}
                        className={`${styles.submenuButton} ${item.enabled ? styles.enabled : styles.disabled}`}
                        onClick={() => handleMenuClick(item)}
                        style={{
                            borderColor: item.enabled ? currentTheme?.activeColor : '#ccc',
                            color: item.enabled ? currentTheme?.activeColor : '#aaa',
                        }}
                    >
                        {t(item.textKey, lang).toUpperCase()}
                        {!item.enabled && (
                            <span className={styles.comingSoonBadge}>
                                {t('comingSoon', lang)}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}
